import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeftIcon,
  TruckIcon,
  CalendarIcon,
  MapPinIcon,
  UserIcon,
  DocumentTextIcon,
  ArrowPathIcon,
  BuildingStorefrontIcon,
  CubeIcon,
  ClipboardDocumentListIcon,
  ShoppingCartIcon,
  CheckCircleIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';
import StatusBadge from '../components/StatusBadge';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const ConsignmentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [consignment, setConsignment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showOrdersModal, setShowOrdersModal] = useState(false);
  const [showCompleteSaleDialog, setShowCompleteSaleDialog] = useState(false);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [processingSale, setProcessingSale] = useState(false);
  const [returnItems, setReturnItems] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [orderPagination, setOrderPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0
  });

  useEffect(() => {
    fetchConsignment();
    fetchOrdersForConsignment();
  }, [id]);

  const fetchConsignment = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/consignments/${id}`);
      setConsignment(response.data);
      
      if (response.data.items) {
        setReturnItems(response.data.items.map(item => ({
          consignment_item_id: item.id,
          quantity_cases: 0,
          quantity_bottles: 0,
          max_cases: item.quantity_cases_issued - item.quantity_cases_sold - item.quantity_cases_returned,
          max_bottles: item.quantity_bottles_issued - item.quantity_bottles_sold - item.quantity_bottles_returned,
          product_name: item.product_name
        })));
      }
    } catch (error) {
      toast.error('Failed to fetch consignment details');
      navigate('/consignments');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrdersForConsignment = async () => {
    try {
      setOrdersLoading(true);
      const response = await api.get('/orders/', { 
        params: { 
          consignment_id: id,
          per_page: 100
        } 
      });
      const linkedOrders = (response.data.items || []).filter(order => 
        order.consignments?.some(c => c.consignment_id === id)
      );
      setOrders(linkedOrders);
      setOrderPagination(prev => ({
        ...prev,
        total: linkedOrders.length
      }));
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleProcessOrderSale = async () => {
    setProcessingSale(true);
    try {
      const order = orders[0]; // Process the first linked order
      const response = await api.post(`/orders/${order.id}/process-sale`);
      toast.success('Sale processed successfully!');
      setShowCompleteSaleDialog(false);
      fetchConsignment();
      fetchOrdersForConsignment();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to process sale');
    } finally {
      setProcessingSale(false);
    }
  };

  const handleReturn = async () => {
    const invalidItems = returnItems.filter(item => 
      item.quantity_cases > item.max_cases || item.quantity_bottles > item.max_bottles
    );

    if (invalidItems.length > 0) {
      toast.error('Return quantities exceed available stock');
      return;
    }

    const hasReturns = returnItems.some(item => 
      item.quantity_cases > 0 || item.quantity_bottles > 0
    );

    if (!hasReturns) {
      toast.error('Please enter at least one item to return');
      return;
    }

    setProcessing(true);
    try {
      const returnData = {
        returned_items: returnItems
          .filter(item => item.quantity_cases > 0 || item.quantity_bottles > 0)
          .map(item => ({
            consignment_item_id: item.consignment_item_id,
            quantity_cases: item.quantity_cases,
            quantity_bottles: item.quantity_bottles
          }))
      };

      await api.post(`/consignments/${id}/return`, returnData);
      toast.success('Consignment returned successfully');
      setShowReturnModal(false);
      fetchConsignment();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to return consignment');
    } finally {
      setProcessing(false);
    }
  };

  const updateReturnItem = (index, field, value) => {
    const newItems = [...returnItems];
    newItems[index][field] = Math.max(0, parseInt(value) || 0);
    setReturnItems(newItems);
  };

  const handleViewOrder = (orderId) => {
    navigate(`/orders/${orderId}`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!consignment) return null;

  const canReturn = consignment.status === 'issued' && 
    (user?.user_type === 'sales_agent' || user?.user_type === 'manager' || user?.user_type === 'owner');

  const totalIssued = consignment.items?.reduce((sum, item) => 
    sum + item.quantity_cases_issued, 0
  ) || 0;

  const totalReturned = consignment.items?.reduce((sum, item) => 
    sum + item.quantity_cases_returned, 0
  ) || 0;

  const totalSold = consignment.items?.reduce((sum, item) => 
    sum + item.quantity_cases_sold, 0
  ) || 0;

  const remaining = totalIssued - totalSold - totalReturned;
  
  const hasLinkedOrders = orders.length > 0;
  const hasUnprocessedOrder = hasLinkedOrders && orders[0]?.status !== 'completed';

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/consignments')}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4 transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Consignments
        </button>
        
        <div className="md:flex md:items-center md:justify-between bg-white rounded-lg shadow-sm p-6">
          <div className="flex-1 min-w-0">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-12 w-12 bg-primary-100 rounded-lg flex items-center justify-center">
                  <TruckIcon className="h-6 w-6 text-primary-600" />
                </div>
              </div>
              <div className="ml-4">
                <div className="flex items-center gap-3">
                  <h2 className="text-2xl font-bold text-gray-900">
                    {consignment.consignment_number}
                  </h2>
                  <StatusBadge status={consignment.status} />
                  {hasLinkedOrders && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Order Consignment
                    </span>
                  )}
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Created on {format(new Date(consignment.created_at), 'dd MMM yyyy HH:mm')}
                </p>
              </div>
            </div>
          </div>
          <div className="mt-4 md:mt-0 flex space-x-3">
            {canReturn && consignment.status === 'issued' && remaining > 0 && !hasLinkedOrders && (
              <button
                onClick={() => setShowReturnModal(true)}
                className="btn-primary inline-flex items-center"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Process Return
              </button>
            )}
            {hasUnprocessedOrder && user?.user_type === 'sales_agent' && (
              <button
                onClick={() => setShowCompleteSaleDialog(true)}
                className="btn-primary inline-flex items-center"
                disabled={processingSale}
              >
                <CurrencyDollarIcon className="h-4 w-4 mr-2" />
                {processingSale ? 'Processing...' : 'Complete Sale'}
              </button>
            )}
            <button
              onClick={() => setShowOrdersModal(true)}
              className="btn-secondary inline-flex items-center"
            >
              <ClipboardDocumentListIcon className="h-4 w-4 mr-2" />
              View Orders ({orders.length})
            </button>
          </div>
        </div>
      </div>

      {/* Order Info Banner for Order Consignments */}
      {hasLinkedOrders && orders[0] && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3">
              <ShoppingCartIcon className="h-5 w-5 text-blue-500 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-800">Linked Order: {orders[0].order_number}</p>
                <p className="text-sm text-blue-600 mt-1">
                  Customer: {orders[0].shop_owner_name} | Total: ZMW {orders[0].total_amount?.toLocaleString()}
                </p>
                <p className="text-xs text-blue-500 mt-1">
                  Order Date: {format(new Date(orders[0].order_date), 'dd MMM yyyy')}
                  {orders[0].delivery_date && ` | Delivery Date: ${format(new Date(orders[0].delivery_date), 'dd MMM yyyy')}`}
                </p>
              </div>
            </div>
            <StatusBadge status={orders[0].status} />
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-4 mb-6">
        <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate flex items-center">
              <CubeIcon className="h-4 w-4 mr-2 text-gray-400" />
              Total Issued
            </dt>
            <dd className="mt-1 text-3xl font-semibold text-gray-900">{totalIssued} cases</dd>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate flex items-center">
              <CubeIcon className="h-4 w-4 mr-2 text-green-400" />
              Sold
            </dt>
            <dd className="mt-1 text-3xl font-semibold text-green-600">{totalSold} cases</dd>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate flex items-center">
              <CubeIcon className="h-4 w-4 mr-2 text-blue-400" />
              Returned
            </dt>
            <dd className="mt-1 text-3xl font-semibold text-blue-600">{totalReturned} cases</dd>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate flex items-center">
              <CubeIcon className="h-4 w-4 mr-2 text-yellow-400" />
              Remaining
            </dt>
            <dd className={`mt-1 text-3xl font-semibold ${remaining > 0 ? 'text-yellow-600' : 'text-green-600'}`}>
              {remaining} cases
            </dd>
          </div>
        </div>
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Basic Information */}
        <div className="bg-white shadow-sm overflow-hidden rounded-lg border border-gray-200">
          <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <dl className="space-y-4">
              <div className="flex items-start">
                <dt className="text-sm font-medium text-gray-500 w-24 flex items-center">
                  <BuildingStorefrontIcon className="h-4 w-4 mr-2 text-gray-400" />
                  Depot:
                </dt>
                <dd className="text-sm text-gray-900 flex-1">
                  <span className="font-medium">{consignment.depot_name}</span>
                  <span className="text-gray-500 ml-2">({consignment.depot_location})</span>
                </dd>
              </div>
              <div className="flex items-start">
                <dt className="text-sm font-medium text-gray-500 w-24 flex items-center">
                  <UserIcon className="h-4 w-4 mr-2 text-gray-400" />
                  Agent:
                </dt>
                <dd className="text-sm text-gray-900 flex-1 font-medium">{consignment.sales_agent_name}</dd>
              </div>
              <div className="flex items-start">
                <dt className="text-sm font-medium text-gray-500 w-24 flex items-center">
                  <MapPinIcon className="h-4 w-4 mr-2 text-gray-400" />
                  To:
                </dt>
                <dd className="text-sm text-gray-900 flex-1">{consignment.destination_town}</dd>
              </div>
              <div className="flex items-start">
                <dt className="text-sm font-medium text-gray-500 w-24 flex items-center">
                  <CalendarIcon className="h-4 w-4 mr-2 text-gray-400" />
                  Issued:
                </dt>
                <dd className="text-sm text-gray-900 flex-1">
                  {format(new Date(consignment.issued_date), 'dd MMM yyyy')}
                </dd>
              </div>
              {consignment.expected_return_date && (
                <div className="flex items-start">
                  <dt className="text-sm font-medium text-gray-500 w-24 flex items-center">
                    <CalendarIcon className="h-4 w-4 mr-2 text-gray-400" />
                    Expected:
                  </dt>
                  <dd className="text-sm text-gray-900 flex-1">
                    {format(new Date(consignment.expected_return_date), 'dd MMM yyyy')}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        </div>

        {/* Items */}
        <div className="bg-white shadow-sm overflow-hidden rounded-lg border border-gray-200">
          <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Items</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Issued
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sold
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Returned
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Balance
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {consignment.items?.map((item) => {
                  const balance = item.quantity_cases_issued - 
                    item.quantity_cases_sold - item.quantity_cases_returned;
                  
                  return (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {item.product_name}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-center font-medium">
                        {item.quantity_cases_issued}
                      </td>
                      <td className="px-4 py-3 text-sm text-green-600 text-center font-medium">
                        {item.quantity_cases_sold}
                      </td>
                      <td className="px-4 py-3 text-sm text-blue-600 text-center font-medium">
                        {item.quantity_cases_returned}
                      </td>
                      <td className="px-4 py-3 text-sm text-center font-medium">
                        <span className={`inline-flex items-center justify-center px-2 py-1 rounded-full text-xs ${
                          balance > 0 ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {balance}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Notes */}
        {consignment.notes && (
          <div className="bg-white shadow-sm overflow-hidden rounded-lg border border-gray-200 lg:col-span-2">
            <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <DocumentTextIcon className="h-5 w-5 mr-2 text-gray-400" />
                Notes
              </h3>
            </div>
            <div className="px-4 py-5 sm:p-6">
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{consignment.notes}</p>
            </div>
          </div>
        )}
      </div>

      {/* Complete Sale Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showCompleteSaleDialog}
        onClose={() => setShowCompleteSaleDialog(false)}
        onConfirm={handleProcessOrderSale}
        title="Complete Order Sale"
        message={`Are you sure you want to complete the sale for order ${orders[0]?.order_number}? This will create a sales transaction and mark the order as completed.`}
        confirmText="Complete Sale"
        type="success"
      />

      {/* Return Modal */}
      <Modal
        isOpen={showReturnModal}
        onClose={() => setShowReturnModal(false)}
        title="Process Return"
        size="lg"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-500 bg-blue-50 p-3 rounded-lg">
            Enter the quantities being returned for each item. Only items with available stock are shown.
          </p>
          
          {returnItems.filter(item => item.max_cases > 0 || item.max_bottles > 0).length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No items available for return.
            </div>
          ) : (
            returnItems.map((item, index) => {
              if (item.max_cases === 0 && item.max_bottles === 0) return null;
              
              return (
                <div key={item.consignment_item_id} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <p className="font-medium text-gray-900 mb-2">{item.product_name}</p>
                  <p className="text-sm text-gray-500 mb-3">
                    Available to return: {item.max_cases} cases, {item.max_bottles} bottles
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500">Cases to Return</label>
                      <input
                        type="number"
                        min="0"
                        max={item.max_cases}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        value={item.quantity_cases}
                        onChange={(e) => updateReturnItem(index, 'quantity_cases', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500">Bottles to Return</label>
                      <input
                        type="number"
                        min="0"
                        max={item.max_bottles}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        value={item.quantity_bottles}
                        onChange={(e) => updateReturnItem(index, 'quantity_bottles', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              );
            })
          )}

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => setShowReturnModal(false)}
              className="btn-secondary"
              disabled={processing}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleReturn}
              className="btn-primary"
              disabled={processing || returnItems.filter(item => item.max_cases > 0 || item.max_bottles > 0).length === 0}
            >
              {processing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Processing...
                </>
              ) : (
                'Process Return'
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Orders Modal */}
      <Modal
        isOpen={showOrdersModal}
        onClose={() => setShowOrdersModal(false)}
        title={`Orders for ${consignment.consignment_number}`}
        size="xl"
      >
        <div className="space-y-4">
          {orders.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <ShoppingCartIcon className="h-12 w-12 mx-auto text-gray-400 mb-3" />
              <p>No orders linked to this consignment</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order #</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sale Status</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {orders.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-primary-600">
                        {order.order_number}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {format(new Date(order.order_date), 'dd MMM yyyy')}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {order.shop_owner_name}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-medium">
                        ZMW {order.total_amount?.toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={order.status} />
                      </td>
                      <td className="px-4 py-3">
                        {order.status === 'completed' ? (
                          <span className="text-green-600 text-sm font-medium">Completed</span>
                        ) : (
                          <span className="text-yellow-600 text-sm font-medium">Pending</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleViewOrder(order.id)}
                          className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default ConsignmentDetails;